import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import SendIcon from '@mui/icons-material/Send'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import { message } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import CircularLoading from '../../../../../../../components/CircularProgress'
import { Tag } from '../../../../../../../components/Tag'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../../constants/errors'
import {
  ONLY_DATE_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../../../../../constants/global'
import { axiosInstance } from '../../../../../../../hooks/useAxios'
import {
  ILead,
  ILeadComment,
  ILeadPipelineItem,
} from '../../../../../../../types/lead'
import { MoveLeadFormModal } from '../../../components/MoveLeadFormModal'
import TagInputForm from './LeadTags'
import { FiClock, FiUser } from 'react-icons/fi'

interface FilterGroupProps {
  onUpdateLeadSuccess: () => void
  isLoading: boolean
  data?: ILead
  isFinished?: boolean
}

const LeadDetailCard = ({
  onUpdateLeadSuccess,
  isLoading,
  data,
  isFinished,
}: FilterGroupProps) => {
  const [description, setDescription] = useState<string>('')
  const [isModalMoveLead, setIsModalMoveLead] = useState<boolean>(false)
  const [selectStepper, setSelectStepper] = useState<string>()
  const [isLoadingAction, setIsLoadingAction] = useState<boolean>(false)
  const [tags, setTags] = useState<string[]>(data?.tags || [])

  useEffect(() => {
    setTags(data?.tags || [])
  }, [data])

  const handleSendDescription = async () => {
    setIsLoadingAction(true)

    try {
      if (!description) {
        message.error('กรุณากรอกคำอธิบาย')
        setIsLoadingAction(false)
        return
      }
      await axiosInstance.post(`/lead/change-active-pipeline-item`, {
        objectId: data?.objectId,
        activePipelineItemObjectId: selectStepper,
        note: description,
      })
      message.success('เปลี่ยนสถานะ pipeline สำเร็จ')
      setSelectStepper('')
      setDescription('')
      onUpdateLeadSuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
    setIsLoadingAction(false)
  }

  const handleSaveTags = async () => {
    //message.success('บันทึกแท็กสำเร็จ')
    setIsLoadingAction(true)
    try {
      await axiosInstance.put(`/lead/tags/${data?.objectId}`, {
        tags: tags,
      })
      message.success('บันทึกแท็กสำเร็จ')
      onUpdateLeadSuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
    setIsLoadingAction(false)
  }

  const pipelineItems = data?.pipelineItems || []
  const cuurrentActivePipelineItemIndex =
    data?.pipelineItems.findIndex(
      (item: ILeadPipelineItem) =>
        item.objectId === data?.activePipelineItemObjectId,
    ) || 0

  const cardColor = (text: string): string => {
    return '#fff'
    if (text.includes('เปลี่ยนสถานะเป็น')) {
      return '#FFFBDE'
    }
    if (text.includes('ย้าย Pipeline เป็น')) {
      return '#FFF2EB'
    }
    return '#fff'
  }

  return (
    <>
      <CircularLoading isLoading={isLoading} />
      <MoveLeadFormModal
        objectIds={data?.objectId ? [data?.objectId] : []}
        isOpen={isModalMoveLead}
        onClose={(visible: boolean) => setIsModalMoveLead(visible)}
        onUpdateLeadListSuccess={onUpdateLeadSuccess}
      />
      <Card sx={{ padding: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={12}>
            <Grid container spacing={2}>
              <Grid item xs={6} lg={3}>
                <Typography variant="h6" color="text.secondary">
                  ชื่อ Lead
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" color="text.primary">
                  {data?.name}
                </Typography>
              </Grid>
              <Grid item xs={6} lg={3}>
                <Typography variant="h6" color="text.secondary">
                  แหล่งที่มา
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    marginTop: 1,
                  }}
                >
                  <Typography variant="subtitle2">
                    {data?.distribution?.name || 'ไม่ระบุ'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} lg={3}>
                <Typography variant="h6" color="text.secondary">
                  สร้างเมื่อวันที่
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" color="text.primary">
                  {dayjs(data?.createdAt).format(ONLY_DATE_FORMAT)}{' '}
                  {dayjs(data?.createdAt).format(ONLY_TIME_FORMAT)} น.
                </Typography>
              </Grid>
              <Grid item xs={6} lg={3} sx={{ textAlign: 'right' }}>
                <Typography variant="h6" color="text.secondary">
                  ดูแลโดย
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" color="text.primary">
                  {data?.assignTo?.name}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={12}>
            <Stepper
              sx={{
                overflowX: 'auto',
              }}
              activeStep={
                data?.pipelineItems.findIndex(
                  (item: ILeadPipelineItem) =>
                    item.objectId === data?.activePipelineItemObjectId,
                ) || 0
              }
              alternativeLabel
            >
              {data?.pipelineItems.map(
                (item: ILeadPipelineItem, index: number) => {
                  return (
                    <Step key={index}>
                      <StepLabel>
                        <Typography variant="subtitle1" color="text.secondary">
                          {item.name}
                        </Typography>
                      </StepLabel>
                    </Step>
                  )
                },
              )}
            </Stepper>
          </Grid>
          <Grid item xs={12} lg={12}>
            <TagInputForm
              tags={tags}
              setTags={(tags: string[]) => setTags(tags)}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <Button
              disabled={isFinished}
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSaveTags}
            >
              บันทึกแท็ก
            </Button>
          </Grid>
          <Grid item xs={12} lg={12}>
            <TextField
              disabled={isFinished}
              multiline={true}
              rows={3}
              fullWidth
              label="คำอธิบาย"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <Grid container justifyContent={'space-between'} spacing={1}>
              <Grid item xs={12} lg={6}>
                <Grid container spacing={1}>
                  <Grid item xs={12} lg={6}>
                    <FormControl variant="filled" fullWidth>
                      <InputLabel id="selectStepper-label">
                        เปลี่ยนสถานะ Pipeline
                      </InputLabel>
                      <Select
                        id="selectStepper"
                        name="selectStepper"
                        labelId="selectStepper-label"
                        value={selectStepper}
                        onChange={(e) => setSelectStepper(e.target.value)}
                        fullWidth
                      >
                        <MenuItem value={''} selected>
                          <em>เลือกสถานะ</em>
                        </MenuItem>
                        {data?.pipelineItems
                          .map((item: ILeadPipelineItem, index: number) => {
                            if (index > cuurrentActivePipelineItemIndex) {
                              return (
                                <MenuItem value={item.objectId} key={index}>
                                  {item.name}
                                </MenuItem>
                              )
                            }
                          })
                          .filter(Boolean)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <Button
                      fullWidth
                      sx={{
                        height: '100%',
                      }}
                      variant="contained"
                      size="medium"
                      startIcon={<SendIcon />}
                      color={'primary'}
                      onClick={handleSendDescription}
                      disabled={isLoadingAction || isFinished}
                    >
                      บันทึก
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6} lg={3}>
                <Button
                  fullWidth
                  sx={{
                    height: '100%',
                  }}
                  variant="contained"
                  size="medium"
                  startIcon={<DriveFileMoveIcon />}
                  color={'warning'}
                  disabled={isFinished}
                  onClick={() => setIsModalMoveLead(true)}
                >
                  ย้าย Pipeline
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Card>
      <Grid container spacing={2}>
        {data?.comments
          .sort(
            (a: ILeadComment, b: ILeadComment) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )
          .map((comment: ILeadComment, index: number) => (
            <Grid item xs={12} lg={12} key={index}>
              <Card
                sx={{
                  backgroundColor: cardColor(comment?.note),
                }}
              >
                <CardHeader
                  title={
                    <Typography
                      variant="subtitle1"
                      color="#999"
                      fontSize={14}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      lineHeight={1}
                    >
                      <FiUser /> {comment?.actor}
                    </Typography>
                  }
                  sx={{
                    padding: '15px 24px 3px 24px',
                  }}
                  action={
                    <Typography
                      variant="subtitle1"
                      fontSize={14}
                      display="flex"
                      alignItems="center"
                      color="#aaa"
                      gap={1}
                      lineHeight={1}
                    >
                      <FiClock />{' '}
                      {dayjs(comment?.createdAt).format(ONLY_DATE_FORMAT)}{' '}
                      {dayjs(comment?.createdAt).format(ONLY_TIME_FORMAT)} น.
                    </Typography>
                  }
                />
                <Divider sx={{ my: 1 }} />
                <CardContent
                  sx={{
                    padding: '0px 24px 0px 24px',
                    paddingBottom: '15px !important',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontSize={17}
                    color="text.primary"
                  >
                    <div style={{ whiteSpace: 'pre-line' }}>
                      {comment?.note}
                    </div>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>
    </>
  )
}

export default LeadDetailCard
